import { Button, Logo } from "@xola/ui-kit";
import { DateFormat, formatDate, now } from "common";
import React from "react";
import { useHistory } from "react-router-dom";
import { Announcement, AnnouncementFeedback, AnnouncementType, Permission, Plugin } from "types";
import { elrondUri } from "../../api/elrondApi";
import { xolaUri } from "../../api/xolaUri";
import { useCollection } from "../../hooks/useCollection";
import { IsGranted } from "../Common/IsGranted";
import { NoTranslate } from "../Common/NoTranslate";
import { AnnouncementActions } from "./AnnouncementActions";
import { useMenuSidebarStore } from "@/stores/menuSidebarStore";

interface AnnouncementsDrawerProps {
    announcements: Announcement[];
    upgrades: Plugin[];
}

export const AnnouncementsDrawer = ({ announcements, upgrades }: AnnouncementsDrawerProps) => {
    return (
        <div className="space-y-5 divide-y divide-gray-lighter">
            {upgrades.map((upgrade) => (
                <AppUpdateTile key={upgrade.id} plugin={upgrade} />
            ))}
            {announcements.map((announcement) => (
                <AnnouncementTile key={announcement.id} announcement={announcement} />
            ))}
        </div>
    );
};

const AppUpdateTile = ({ plugin }: { plugin: Plugin }) => {
    const history = useHistory();
    const handleUpgradeClick = () => {
        history.push(`/apps/${plugin.id}`);
        useMenuSidebarStore.setState({ shouldCloseDrawer: true });
    };

    return (
        <IsGranted permissions={Permission.APP_STORE_VIEW}>
            <div className="flex flex-col space-y-1 pt-5">
                <div className="flex space-x-2">
                    <Logo src={plugin.logo ? getPluginPhotoUrl(plugin) : getDefaultPhotoUrl()} />
                    <div>
                        <NoTranslate className="text-sm">{formatDate(now(), DateFormat.DATE)}</NoTranslate>
                        <NoTranslate as="h4" className="font-bold">
                            {plugin.name}
                        </NoTranslate>
                    </div>
                </div>
                <div className="text-sm">
                    A new version of <var data-var="pluginname">{plugin.name}</var>{" "}
                    <var data-var="pluginversion">{plugin.version}</var> is available
                </div>
                <div>
                    <Button key={plugin.id} type="primary" size="small" onClick={handleUpgradeClick}>
                        Upgrade
                    </Button>
                </div>
            </div>
        </IsGranted>
    );
};

const AnnouncementTile = ({ announcement }: { announcement: Announcement }) => {
    return (
        <div className="flex flex-col space-y-1 pt-5">
            <div className="flex space-x-2">
                <Logo src={announcement.photo ? getPhotoUrl(announcement) : getDefaultPhotoUrl()} />
                <div>
                    <NoTranslate className="text-sm">{formatDate(announcement.createdAt, DateFormat.DATE)}</NoTranslate>
                    <NoTranslate as="h4" className="font-bold">
                        {announcement.title}
                    </NoTranslate>
                </div>
            </div>
            <NoTranslate as="div" className="text-sm">
                {announcement.description}
            </NoTranslate>
            <AnnouncementActions announcement={announcement} className="flex-col space-y-1" />
        </div>
    );
};

export const getDefaultPhotoUrl = () => {
    return `${xolaUri}/cache/images/placeholder_268x200.jpg`;
};

export const getPhotoUrl = (announcement: Announcement) => {
    return `${xolaUri}/${announcement.photo?.src}`;
};

export const getPluginPhotoUrl = (plugin: Plugin) => {
    return `${elrondUri}${plugin.logo?.url}`;
};

export const getUnseenNotificationsCount = (notices: Announcement[], noticeFeedbacks: AnnouncementFeedback[]) => {
    let unSeenCount = 0;
    const unseenNotices = [];
    for (const notice of notices) {
        if (!isNoticeRead(notice, noticeFeedbacks)) {
            unseenNotices.push(notice);
            unSeenCount++;
        }
    }

    return { unseenNotices, unSeenCount };
};

const isNoticeRead = (notice: Announcement, noticeFeedbacks: AnnouncementFeedback[]) => {
    for (const noticeFeedback of noticeFeedbacks) {
        if (notice.id === noticeFeedback.announcement.id) {
            return true;
        }
    }

    return false;
};

export const useNotices = (filter: any) => {
    const { data: announcements } = useCollection<Announcement>("announcements", filter);
    return announcements?.filter((announcement) => announcement.types.includes(AnnouncementType.NOTICE));
};
